import { useCallback, useEffect, useRef, useState } from "react";
import { FilePlus2, Trash2 } from "lucide-react";
import api from "../lib/api";
import { usePlayer } from "../context/PlayerContext";
import { fetchFromPhone } from "../lib/djXfer";

// The DJ Booth's SCREEN: this is the page that goes on the TV (or on a laptop that is plugged into the TV, or sent to it with AirPlay
// or Chromecast). It plays the video and the sound. It has no buttons to run: the member's phone (pages/DjBooth.js) is the controller.
//
// How it works: the page asks the server for a short code and shows it big. The member types the code on their phone. After that this
// page only does what the server says (load this song on deck 1, play, slide the crossfader...), and tells the server how far along
// each song is. It asks "has anything changed?" and the server answers the moment something does (backend/dj.py).
// The four decks are four <video> tags stacked on top of each other. Decks 1 and 3 are the crossfader's left side, decks 2 and 4 its
// right side. Each deck's sound is its own volume x its side of the crossfader (equal power, so the middle is not quieter) x master.
// The picture is the same mix: the layers are laid over each other so that every deck shows in proportion to its weight (its volume x
// how far the crossfader is on its side), which for two decks is the plain blend of one picture into the other.
// The big Start screen button is needed once, because a browser only lets a page play sound after somebody pressed something on it.
//
// Videos and music of their own: the "Add videos or music from this device" button (bottom left) lets the person pick video or music files on THIS device. The
// browser reads them right here (blob addresses), nothing is uploaded. This page only tells the server a short list (a key and a
// title for each) so the phone can list them; when the phone loads one on a deck the server sends "local" and the key, and this page
// plays its own copy. The list lives in memory only: reloading the page forgets it.
//
// Friends' own files: a friend in the party can load a video or music file from THEIR phone (pages/DjJoin.js). The server then sends a load
// with `phone` and the friend's party id; this page asks that phone for the file (lib/djXfer.js: a WebRTC data channel, through Cloudflare's
// relay when the two cannot reach each other directly), keeps the pieces in memory until it has all of them, makes a blob of them, and plays
// that. Nothing is uploaded or saved. It tells the server when the whole file is here ("ready") or could not be fetched ("failed"); a deck
// cannot be played before that. Up to 600 MB of friends' files are kept in memory (the oldest ones that no deck holds go first), and all of
// it is forgotten when the page is reloaded.

const KEY = "dtm_dj_screen";
const DECKS = 4;
const MAX_FILES = 200;                  // same as the server (backend/dj.py MAX_LOCAL_FILES)
const LOCAL_PREFIX = "local:";
const isVideoFile = (f) => /^video\//.test(f.type || "") || /\.(mp4|m4v|mov|webm)$/i.test(f.name || "");
// music files (MP3, WAV...) play in the same <video> tag; the screen shows the Dan The Man TV astronaut for them
const isMusicFile = (f) => /^audio\//.test(f.type || "") || /\.(mp3|wav|m4a|aac|ogg|oga|opus|flac)$/i.test(f.name || "");
const MUSIC_PICTURE = "https://media.danthemantv.com/dtm-share.jpg";
const PHONE_KEEP_BYTES = 600 * 1024 * 1024;       // friends' files kept in memory at most
const titleOf = (name) => String(name || "Video").replace(/\.[^.]+$/, "").replace(/[_]+/g, " ").replace(/\s+/g, " ").trim() || "Video";
const pageId = Math.random().toString(36).slice(2, 6);      // keys made on this page load never match ones from an earlier load
const newMix = () => ({ x: 0, gl: null, doneN: null, pendingDone: null, first: true, lv: [], pv: [] });
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const clamp01 = (x) => Math.max(0, Math.min(1, x));

function readSaved() {
  try {
    const s = JSON.parse(localStorage.getItem(KEY) || "null");
    return s && s.code && s.token ? s : null;
  } catch { return null; }
}
function keep(s) {
  try { if (s) localStorage.setItem(KEY, JSON.stringify(s)); else localStorage.removeItem(KEY); } catch { /* ignore */ }
}

export default function DjScreen() {
  const { setPlaying } = usePlayer();
  const [unlocked, setUnlocked] = useState(false);
  const [session, setSession] = useState(null);
  const [desired, setDesired] = useState(null);
  const [blocked, setBlocked] = useState(false);
  const [dom, setDom] = useState(0);            // which deck is the louder one: its title shows
  const [welcome, setWelcome] = useState(false);
  const [idle, setIdle] = useState(false);
  const [trouble, setTrouble] = useState("");
  const [files, setFiles] = useState([]);       // videos picked on this device: [{ k, t, sig, url }]
  const [incoming, setIncoming] = useState([]);   // friends' files on their way: [{ vid, title, pct, state: "get" | "fail" }]

  const rootRef = useRef(null);
  const pickRef = useRef(null);
  const filesRef = useRef([]);                  // the same list, for code that must not wait for a render
  const fileCount = useRef(0);
  const vids = useRef(Array.from({ length: DECKS }, () => null));
  const layers = useRef(Array.from({ length: DECKS }, () => null));
  const st = useRef(Array.from({ length: DECKS }, () => ({ n: null, seekN: -1, endedN: null, pendingSeek: null })));
  const want = useRef(null);                    // the newest thing the server asked for
  const sess = useRef(null);
  const mix = useRef(newMix());
  const lastReport = useRef("");
  const wasPaired = useRef(false);
  const idleTimer = useRef(null);
  const phoneBlobs = useRef(new Map());         // friends' files that arrived: vid -> { url, size, at }
  const phoneJobs = useRef(new Map());          // fetches under way: vid -> { promise, cancel }
  const incRef = useRef(new Map());             // what the "getting a file" notes show: vid -> { vid, title, pct, state }
  const applyRef = useRef(null);

  // The site's own player must not play under this page.
  useEffect(() => { setPlaying(false); }, [setPlaying]);

  useEffect(() => {
    const title = document.title;
    document.title = "DJ Screen | Dan The Man TV";
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.title = title; document.body.style.overflow = prev; };
  }, []);

  // Keep the TV awake while the screen is on.
  useEffect(() => {
    if (!unlocked) return undefined;
    let lock = null;
    let dead = false;
    const ask = async () => {
      try {
        if (!dead && navigator.wakeLock && document.visibilityState === "visible") lock = await navigator.wakeLock.request("screen");
      } catch { /* the TV said no: fine */ }
    };
    ask();
    document.addEventListener("visibilitychange", ask);
    return () => { dead = true; document.removeEventListener("visibilitychange", ask); try { if (lock) lock.release(); } catch { /* ignore */ } };
  }, [unlocked]);

  // ---- tell the server how far along each deck is ----
  const sendReport = useCallback((force) => {
    const s = sess.current;
    const w = want.current;
    if (!s || !w || !w.paired) return;
    const m = mix.current;
    const decks = vids.current.map((v, i) => {
      const d = st.current[i];
      if (!v || d.n === null) return { n: null };
      const len = v.duration;
      return {
        n: d.n,
        t: Math.round((v.currentTime || 0) * 10) / 10,
        d: Number.isFinite(len) ? Math.round(len * 10) / 10 : 0,
        p: !v.paused && !v.ended,
        e: d.endedN === d.n || !!v.ended,
      };
    });
    const key = JSON.stringify({ decks, gd: m.pendingDone });
    if (!force && key === lastReport.current) return;
    lastReport.current = key;
    const done = m.pendingDone;
    api.post("/dj/screen/report", { code: s.code, token: s.token, decks, glide_done: done }, { timeout: 8000 })
      .then(() => { if (done && m.pendingDone === done) m.pendingDone = null; })
      .catch(() => { lastReport.current = ""; });
  }, []);

  // ---- friends' own files ----
  const showIncoming = useCallback(() => setIncoming(Array.from(incRef.current.values()).map((x) => ({ ...x }))), []);
  const reportXfer = useCallback((n, state, why) => {
    const s = sess.current;
    if (!s) return;
    api.post("/dj/screen/xfer", { code: s.code, token: s.token, n, state, why: why || null }, { timeout: 8000 }).catch(() => {});
  }, []);
  // the whole file is here: keep it, give it to every deck that was waiting for it, and tell the server
  const storePhone = useCallback((vid, blob) => {
    const map = phoneBlobs.current;
    map.set(vid, { url: URL.createObjectURL(blob), size: blob.size, at: Date.now() });
    let total = 0;
    map.forEach((x) => { total += x.size; });
    if (total > PHONE_KEEP_BYTES) {
      const held = new Set(((want.current && want.current.decks) || []).map((d) => (d.load && d.load.phone ? d.load.vid : null)).filter(Boolean));     // decks still holding one
      const old = Array.from(map.entries()).filter(([k]) => k !== vid && !held.has(k)).sort((a, b) => a[1].at - b[1].at);
      for (const [k, x] of old) {
        if (total <= PHONE_KEEP_BYTES) break;
        try { URL.revokeObjectURL(x.url); } catch { /* ignore */ }
        map.delete(k);
        total -= x.size;
      }
    }
    const w = want.current;
    if (w) {
      w.decks.forEach((d, i) => {
        const s = st.current[i];
        const v = vids.current[i];
        if (d.load && d.load.phone && d.load.vid === vid && s && s.waiting && v && s.n === d.load.n) {
          v.src = map.get(vid).url;
          try { v.load(); } catch { /* ignore */ }
          s.waiting = false;
          if (d.load.state !== "ready") reportXfer(d.load.n, "ready");
        }
      });
    }
    if (applyRef.current) applyRef.current();
  }, [reportXfer]);
  const startPhone = useCallback((load) => {
    const s = sess.current;
    if (!s || phoneJobs.current.has(load.vid)) return;
    const entry = { vid: load.vid, title: load.title || "a file", pct: 0, state: "get" };
    incRef.current.set(load.vid, entry);
    showIncoming();
    let last = 0;
    const wanted = () => !!want.current && want.current.decks.some((d) => d.load && d.load.vid === load.vid && d.load.state === "wait");
    const job = fetchFromPhone({
      api, code: s.code, token: s.token, fid: load.fid, size: load.size, mime: load.mime, isWanted: wanted,
      onProgress: (p) => { entry.pct = p; const now = performance.now(); if (now - last > 250) { last = now; showIncoming(); } },
    });
    phoneJobs.current.set(load.vid, job);
    job.promise.then(({ blob }) => {
      phoneJobs.current.delete(load.vid);
      incRef.current.delete(load.vid);
      showIncoming();
      storePhone(load.vid, blob);
    }).catch((e) => {
      phoneJobs.current.delete(load.vid);
      const why = e && e.why ? e.why : "connect";
      ((want.current && want.current.decks) || []).forEach((d) => { if (d.load && d.load.vid === load.vid && d.load.state === "wait") reportXfer(d.load.n, "failed", why); });
      entry.state = "fail";
      showIncoming();
      setTimeout(() => { if (incRef.current.get(load.vid) === entry) { incRef.current.delete(load.vid); showIncoming(); } }, 8000);
    });
  }, [reportXfer, showIncoming, storePhone]);
  const forgetPhone = useCallback(() => {
    phoneJobs.current.forEach((j) => { try { j.cancel(); } catch { /* ignore */ } });
    phoneJobs.current.clear();
    phoneBlobs.current.forEach((x) => { try { URL.revokeObjectURL(x.url); } catch { /* ignore */ } });
    phoneBlobs.current.clear();
    incRef.current.clear();
    setIncoming([]);
  }, []);
  useEffect(() => () => forgetPhone(), [forgetPhone]);

  // ---- make the two <video> tags do what the server asked ----
  const apply = useCallback(() => {
    const w = want.current;
    if (!w) return;
    w.decks.forEach((d, i) => {
      const v = vids.current[i];
      const s = st.current[i];
      if (!v || !s) return;
      if (!d.load) {
        if (s.n !== null) {
          try { v.pause(); } catch { /* ignore */ }
          v.removeAttribute("poster");
          v.removeAttribute("src");
          try { v.load(); } catch { /* ignore */ }
          s.n = null; s.endedN = null; s.pendingSeek = null;
        }
        return;
      }
      if (s.n !== d.load.n) {
        s.n = d.load.n; s.seekN = d.seek.n; s.endedN = null; s.pendingSeek = null; s.waiting = false;
        if (d.load.cover) v.setAttribute("poster", d.load.cover); else v.removeAttribute("poster");
        let url = d.load.url;
        if (d.load.phone) {                       // a friend's own file: here already, or fetched from their phone now
          const hit = phoneBlobs.current.get(d.load.vid);
          if (hit) {
            hit.at = Date.now();
            url = hit.url;
            if (d.load.state !== "ready") reportXfer(d.load.n, "ready");
          } else {
            url = "";
            s.waiting = true;
            if (d.load.state === "wait") startPhone(d.load);
            else if (d.load.state === "ready") reportXfer(d.load.n, "failed", "lost");        // this page lost it (it was reloaded)
          }
        } else if (d.load.local) {                       // one of the videos picked on this device
          const key = String(d.load.vid || "").slice(LOCAL_PREFIX.length);
          const f = filesRef.current.find((x) => x.k === key);
          url = f ? f.url : "";
          if (!f) setTrouble("A video you added is missing from this screen. Add it again.");
        } else setTrouble("");
        if (url) v.src = url; else v.removeAttribute("src");
        try { v.load(); } catch { /* ignore */ }
      } else if (d.seek.n !== s.seekN) {
        s.seekN = d.seek.n; s.endedN = null;
        if (Number.isFinite(v.duration) && v.duration > 0) { try { v.currentTime = clamp01(d.seek.f) * v.duration; } catch { /* ignore */ } }
        else s.pendingSeek = d.seek.f;
      }
      if (s.waiting) {                              // a friend's file that has not all arrived: nothing to play yet
        if (!v.paused) { try { v.pause(); } catch { /* ignore */ } }
        return;
      }
      if (v.playbackRate !== d.rate) v.playbackRate = d.rate;
      if (d.play) {
        if (v.paused && s.endedN !== s.n) {
          const p = v.play();
          if (p && p.catch) p.catch((e) => { if (e && e.name === "NotAllowedError") setBlocked(true); });
        }
      } else if (!v.paused) {
        try { v.pause(); } catch { /* ignore */ }
      }
    });
  }, [reportXfer, startPhone]);
  applyRef.current = apply;

  // ---- videos of their own, picked on this device ----
  const addFiles = (list) => {
    const have = filesRef.current;
    const sigs = new Set(have.map((f) => f.sig));
    const next = [...have];
    let notVideo = 0;
    Array.from(list || []).forEach((f) => {
      const music = !isVideoFile(f) && isMusicFile(f);
      if (!music && !isVideoFile(f)) { notVideo += 1; return; }
      const sig = `${f.name}|${f.size}|${f.lastModified}`;
      if (sigs.has(sig) || next.length >= MAX_FILES) return;
      sigs.add(sig);
      fileCount.current += 1;
      next.push({ k: `${pageId}${fileCount.current}`, t: titleOf(f.name), a: music, sig, url: URL.createObjectURL(f) });
    });
    if (notVideo && next.length === have.length) setTrouble("Those files are not videos or music. Pick MP4 videos or MP3 / WAV music files.");
    if (next.length !== have.length) { filesRef.current = next; setFiles(next); }
  };
  const clearFiles = () => { filesRef.current = []; setFiles([]); };
  useEffect(() => () => { filesRef.current.forEach((f) => { try { URL.revokeObjectURL(f.url); } catch { /* ignore */ } }); }, []);

  // tell the server the short list (keys and titles only) whenever it changes, and again for a new code
  useEffect(() => {
    if (!session) return undefined;
    let dead = false;
    let timer = null;
    const send = async (tries) => {
      try {
        await api.post("/dj/screen/files", { code: session.code, token: session.token, files: files.map((f) => ({ k: f.k, t: f.t, a: !!f.a })) }, { timeout: 8000 });
      } catch (e) {
        if (dead || tries >= 6 || e?.response?.status === 404) return;
        timer = setTimeout(() => send(tries + 1), 3000);
      }
    };
    send(0);
    return () => { dead = true; clearTimeout(timer); };
  }, [session, files]);

  // ---- get a code (or pick up the one this screen already had) ----
  useEffect(() => {
    if (!unlocked || session) return undefined;
    let dead = false;
    (async () => {
      const saved = readSaved();
      if (saved) { if (!dead) { sess.current = saved; setSession(saved); } return; }
      while (!dead) {
        try {
          const r = await api.post("/dj/screen/new");
          if (dead) return;
          keep(r.data);
          sess.current = r.data;
          setTrouble("");
          setSession(r.data);
          return;
        } catch (e) {
          if (dead) return;
          setTrouble(e?.response?.data?.detail || "Could not reach Dan The Man TV. Trying again...");
          await sleep(4000);
        }
      }
    })();
    return () => { dead = true; };
  }, [unlocked, session]);

  // ---- listen for changes ----
  useEffect(() => {
    if (!session) return undefined;
    let dead = false;
    let since = -1;
    (async () => {
      while (!dead) {
        try {
          const r = await api.get("/dj/screen/wait", { params: { code: session.code, token: session.token, since }, timeout: 30000 });
          if (dead) return;
          setTrouble("");
          since = r.data.v;
          want.current = r.data;
          setDesired(r.data);
          apply();
        } catch (e) {
          if (dead) return;
          if (e?.response?.status === 404) {          // the booth ended (or this screen is too old): start over with a new code
            keep(null);
            forgetPhone();
            sess.current = null;
            want.current = null;
            mix.current = newMix();
            wasPaired.current = false;
            vids.current.forEach((v, i) => {              // whatever was playing stops
              if (v) {
                try { v.pause(); } catch { /* ignore */ }
                v.removeAttribute("poster");
                v.removeAttribute("src");
                try { v.load(); } catch { /* ignore */ }
              }
              st.current[i] = { n: null, seekN: -1, endedN: null, pendingSeek: null };
            });
            setDesired(null);
            setSession(null);
            return;
          }
          setTrouble("Lost the connection. Trying again...");
          await sleep(2000);
        }
      }
    })();
    return () => { dead = true; };
  }, [session, apply, forgetPhone]);

  // ---- a short "connected" note when a phone links up ----
  const paired = !!(desired && desired.paired);
  useEffect(() => {
    if (paired && !wasPaired.current) {
      setWelcome(true);
      const t = setTimeout(() => setWelcome(false), 6000);
      wasPaired.current = true;
      return () => clearTimeout(t);
    }
    if (!paired) wasPaired.current = false;
    return undefined;
  }, [paired]);

  // ---- the mixer: ~30 times a second set the sound and the picture blend ----
  useEffect(() => {
    if (!unlocked) return undefined;
    let last = performance.now();
    const id = setInterval(() => {
      const w = want.current;
      if (!w) return;
      const m = mix.current;
      const now = performance.now();
      const dt = Math.min(0.2, (now - last) / 1000);
      last = now;
      if (m.first) { m.x = w.xfade; m.first = false; }
      const g = w.glide;
      if (g && g.n === m.doneN) {
        m.x = g.to;                                   // a fade that finished: stay put until the server catches up
      } else if (g) {
        if (!m.gl || m.gl.n !== g.n) m.gl = { n: g.n, from: m.x, to: g.to, t0: now, secs: g.secs };
        const p = clamp01((now - m.gl.t0) / (m.gl.secs * 1000));
        m.x = m.gl.from + (m.gl.to - m.gl.from) * (p * p * (3 - 2 * p));
        if (p >= 1) { m.doneN = g.n; m.pendingDone = { n: g.n, x: g.to }; m.gl = null; sendReport(true); }
      } else {
        m.gl = null;
        m.x += (w.xfade - m.x) * (1 - Math.exp(-dt / 0.08));
        if (Math.abs(w.xfade - m.x) < 0.0005) m.x = w.xfade;
      }
      // The last 3% at each end of the crossfader is a clean cut: all the way over means the other side is completely gone, in the
      // sound and in the picture. In between the sound is equal power, and the picture follows a smooth S so the other side is
      // nearly gone well before the end instead of lingering as a ghost.
      const xe = clamp01((clamp01(m.x) - 0.03) / 0.94);
      const px = xe * xe * (3 - 2 * xe);
      const master = clamp01(w.master);
      const gl = Math.cos((xe * Math.PI) / 2);
      const gr = Math.sin((xe * Math.PI) / 2);
      // Which decks are really playing, and can be seen or heard at the crossfader's place. A deck that is paused, has finished or has
      // only just been loaded keeps its last frame on its <video>, but that frame must not show through the deck that plays (decks 1
      // and 3 share the left side, 2 and 4 the right). While at least one deck plays, only the playing decks are in the picture; when
      // nothing plays (or what plays is all the way over on the other side), the loaded decks stay as they are.
      const live = [];
      for (let i = 0; i < DECKS; i += 1) {
        const d = w.decks[i];
        const s = st.current[i];
        const v = vids.current[i];
        const side = i % 2 === 1 ? px : 1 - px;
        const lvl = d && d.level !== undefined ? clamp01(d.level) : 1;
        live[i] = !!(d && d.load && d.play && !(s && s.endedN === d.load.n) && !(v && v.ended) && lvl * side > 0.02);
      }
      const anyLive = live.some(Boolean);
      let cum = 0;
      let best = -1;
      let bestG = 0;
      for (let i = 0; i < DECKS; i += 1) {
        const d = w.decks[i];
        const target = d && d.load ? clamp01(d.level === undefined ? 1 : d.level) : 0;
        if (m.lv[i] === undefined) m.lv[i] = target;
        m.lv[i] += (target - m.lv[i]) * (1 - Math.exp(-dt / 0.08));          // the volume slider glides instead of jumping
        if (Math.abs(target - m.lv[i]) < 0.0005) m.lv[i] = target;
        const right = i % 2 === 1;
        const g = master * m.lv[i] * (right ? gr : gl);
        const v = vids.current[i];
        if (v) v.volume = clamp01(g);
        // the picture: the layers are laid over each other from deck 1 up; a layer's opacity is its weight over all weights so far,
        // which makes the picture the weighted mix of the decks (for two decks that is the plain blend of one into the other)
        const seen = d && d.load ? (anyLive ? (live[i] ? 1 : 0) : 1) : 0;          // in the picture or not
        if (m.pv[i] === undefined) m.pv[i] = seen;
        m.pv[i] += (seen - m.pv[i]) * (1 - Math.exp(-dt / 0.2));                    // a quick dissolve instead of a cut
        if (Math.abs(seen - m.pv[i]) < 0.0005) m.pv[i] = seen;
        const wp = m.lv[i] * m.pv[i] * (right ? px : 1 - px);
        cum += wp;
        const layer = layers.current[i];
        if (layer) layer.style.opacity = wp > 0.0005 ? String(Math.min(1, wp / cum)) : "0";
        if (d && d.load && d.play && g > bestG) { bestG = g; best = i; }
      }
      if (best >= 0) setDom((prev) => (prev === best ? prev : best));
    }, 33);
    return () => clearInterval(id);
  }, [unlocked, sendReport]);

  // ---- report about once a second (only when something changed) ----
  useEffect(() => {
    if (!unlocked) return undefined;
    const id = setInterval(() => sendReport(false), 1000);
    return () => clearInterval(id);
  }, [unlocked, sendReport]);

  // ---- start, full screen, and hiding the pointer ----
  const goFull = () => {
    const el = rootRef.current;
    try {
      if (document.fullscreenElement) document.exitFullscreen();
      else if (el && el.requestFullscreen) el.requestFullscreen().catch(() => {});
    } catch { /* ignore */ }
  };
  const start = () => {
    setUnlocked(true);
    const el = rootRef.current;
    try { if (el && el.requestFullscreen && !document.fullscreenElement) el.requestFullscreen().catch(() => {}); } catch { /* ignore */ }
  };
  const wake = () => {
    setIdle(false);
    clearTimeout(idleTimer.current);
    idleTimer.current = setTimeout(() => setIdle(true), 3500);
  };
  useEffect(() => () => clearTimeout(idleTimer.current), []);

  const onMeta = (i) => {
    const v = vids.current[i];
    const s = st.current[i];
    if (v && s && s.pendingSeek !== null && Number.isFinite(v.duration) && v.duration > 0) {
      try { v.currentTime = clamp01(s.pendingSeek) * v.duration; } catch { /* ignore */ }
      s.pendingSeek = null;
    }
  };
  const onEnded = (i) => { st.current[i].endedN = st.current[i].n; sendReport(true); };

  const anyLoad = !!(desired && desired.decks.some((d) => d.load));
  const domDeck = desired && desired.decks[dom];
  const title = domDeck && domDeck.load && domDeck.play ? domDeck.load : null;
  const host = typeof window !== "undefined" ? window.location.host : "danthemantv.com";

  return (
    <div
      ref={rootRef}
      data-testid="dj-screen"
      onMouseMove={wake}
      className={`fixed inset-0 z-[2147483000] bg-black text-white overflow-hidden select-none ${idle ? "cursor-none" : ""}`}
    >
      <div className="absolute inset-0">
        {Array.from({ length: DECKS }, (_, i) => (
          <div key={i} ref={(el) => { layers.current[i] = el; }} className={`absolute inset-0 ${i === 0 ? "opacity-100" : "opacity-0"}`}>
            <video
              ref={(el) => { vids.current[i] = el; }}
              data-testid={`dj-screen-video-${i}`}
              className="w-full h-full object-contain bg-black"
              playsInline
              preload="auto"
              controlsList="nodownload"
              onContextMenu={(e) => e.preventDefault()}
              onLoadedMetadata={() => onMeta(i)}
              onEnded={() => onEnded(i)}
              onError={() => { if (st.current[i].n !== null) setTrouble("One of the files would not play."); }}
            />
            {desired && desired.decks[i] && desired.decks[i].load && desired.decks[i].load.audio && (
              <img src={MUSIC_PICTURE} alt="" draggable={false} data-testid={`dj-screen-art-${i}`}
                className="absolute inset-0 w-full h-full object-contain bg-black pointer-events-none" />
            )}
          </div>
        ))}
      </div>

      {unlocked && paired && !anyLoad && (
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-8 bg-gradient-to-b from-zinc-950 via-black to-zinc-950" data-testid="dj-screen-idle">
          <div className="text-zinc-500 tracking-[0.3em] text-sm sm:text-base mb-4">DAN THE MAN TV · DJ BOOTH</div>
          <div className="text-3xl sm:text-5xl font-extrabold">Ready</div>
          <div className="text-zinc-400 text-lg sm:text-2xl mt-3">Pick a song on your phone and press play.</div>
          <div className="text-zinc-500 text-base sm:text-xl mt-6 max-w-3xl">Want to play videos or music saved on this device? Press <span className="text-zinc-300">Add videos or music from this device</span> (bottom left; move the mouse if you do not see it).</div>
        </div>
      )}

      {title && (
        <div className="absolute left-0 right-0 bottom-0 px-8 pb-8 pt-24 bg-gradient-to-t from-black/80 to-transparent pointer-events-none" data-testid="dj-screen-title">
          <div className="text-2xl sm:text-4xl font-extrabold truncate">{title.title}</div>
          {title.artist ? <div className="text-lg sm:text-2xl text-zinc-300 truncate">{title.artist}</div> : null}
        </div>
      )}

      {unlocked && !paired && (
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-8 bg-gradient-to-b from-zinc-950 via-black to-zinc-950" data-testid="dj-screen-code">
          <div className="text-zinc-500 tracking-[0.3em] text-sm sm:text-base mb-6">DAN THE MAN TV · DJ BOOTH</div>
          {session ? (
            <>
              <div className="text-zinc-300 text-xl sm:text-3xl mb-4">Your code</div>
              <div className="font-extrabold tracking-[0.18em] text-cyan-300 text-[16vw] leading-none" data-testid="dj-screen-code-text">{session.code}</div>
              <div className="text-zinc-400 text-lg sm:text-2xl mt-8 max-w-3xl">
                On your phone, open <span className="text-white font-semibold">{host}/dj</span> and type this code. You need a Premium login.
              </div>
              <div className="text-zinc-500 text-base sm:text-xl mt-5 max-w-3xl">Want to play videos or music saved on this device? Press <span className="text-zinc-300">Add videos or music from this device</span> (bottom left; move the mouse if you do not see it).</div>
            </>
          ) : (
            <div className="text-zinc-300 text-xl sm:text-3xl">Getting a code...</div>
          )}
        </div>
      )}

      {unlocked && incoming.length > 0 && (
        <div className="absolute top-24 left-0 right-0 flex flex-col items-center gap-2 pointer-events-none px-6" data-testid="dj-screen-incoming">
          {incoming.map((x) => (
            <div key={x.vid} data-testid="dj-screen-incoming-row"
              className={`px-6 py-3 rounded-full bg-black/70 border border-white/15 text-lg sm:text-2xl max-w-full truncate ${x.state === "fail" ? "text-amber-300" : ""}`}>
              {x.state === "fail" ? `Could not get “${x.title}” from a phone` : `Getting “${x.title}” from a phone · ${Math.round(x.pct * 100)}%`}
            </div>
          ))}
        </div>
      )}

      {welcome && paired && (
        <div className="absolute top-6 left-0 right-0 flex justify-center pointer-events-none" data-testid="dj-screen-welcome">
          <div className="px-6 py-3 rounded-full bg-black/70 border border-white/15 text-lg sm:text-2xl">
            Connected{desired.host_name ? `: ${desired.host_name}'s phone` : ""}
          </div>
        </div>
      )}

      {trouble && unlocked && (
        <div className="absolute top-6 right-6 px-4 py-2 rounded-full bg-black/70 border border-white/15 text-sm sm:text-lg text-amber-300" data-testid="dj-screen-trouble">{trouble}</div>
      )}

      <input ref={pickRef} type="file" accept="video/*,audio/*,.mp3,.wav,.m4a,.aac,.ogg,.flac" multiple hidden data-testid="dj-screen-add-input"
        onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
      {unlocked && !idle && (
        <div className="absolute bottom-5 left-5 flex items-center gap-2 flex-wrap max-w-[60%]" data-testid="dj-screen-files-bar">
          <button type="button" onClick={() => pickRef.current && pickRef.current.click()} data-testid="dj-screen-add"
            className="px-4 py-2 rounded-full bg-black/60 border border-white/20 text-sm text-zinc-200 hover:bg-black/80 flex items-center gap-2">
            <FilePlus2 className="w-4 h-4" /> Add videos or music from this device
          </button>
          {files.length > 0 && (
            <>
              <span className="text-sm text-zinc-300 px-2 py-1 rounded-full bg-black/50" data-testid="dj-screen-file-count">
                {files.length} {files.length === 1 ? "file" : "files"} added
              </span>
              <button type="button" onClick={clearFiles} data-testid="dj-screen-clear"
                className="px-3 py-2 rounded-full bg-black/60 border border-white/20 text-sm text-zinc-300 hover:bg-black/80 flex items-center gap-1.5">
                <Trash2 className="w-4 h-4" /> Clear
              </button>
            </>
          )}
        </div>
      )}
      {unlocked && !idle && (
        <button type="button" onClick={goFull} data-testid="dj-screen-full"
          className="absolute bottom-5 right-5 px-4 py-2 rounded-full bg-black/60 border border-white/20 text-sm text-zinc-200 hover:bg-black/80">
          Full screen
        </button>
      )}

      {blocked && (
        <button type="button" autoFocus onClick={() => { setBlocked(false); apply(); }} data-testid="dj-screen-unblock"
          className="absolute inset-0 flex items-center justify-center bg-black/80 text-3xl sm:text-5xl font-extrabold">
          Press OK to start the music
        </button>
      )}

      {!unlocked && (
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-8 bg-gradient-to-b from-zinc-950 via-black to-zinc-950">
          <div className="text-zinc-500 tracking-[0.3em] text-sm sm:text-base mb-6">DAN THE MAN TV</div>
          <h1 className="text-4xl sm:text-6xl font-extrabold mb-4">DJ Booth screen</h1>
          <p className="text-zinc-400 text-lg sm:text-2xl max-w-3xl mb-10">This screen shows the video and plays the sound. Your phone is the controller.</p>
          <button type="button" autoFocus onClick={start} data-testid="dj-screen-start"
            className="px-10 py-5 rounded-full bg-cyan-400 text-black text-2xl sm:text-4xl font-extrabold hover:bg-cyan-300 focus:outline-none focus:ring-8 focus:ring-cyan-200/60">
            Start screen
          </button>
          <p className="text-zinc-500 text-base sm:text-xl mt-6">Press OK on your remote, or click.</p>
        </div>
      )}
    </div>
  );
}
